import { useManager } from 'dreamstate';
import React, { useRef, useState, useCallback, ChangeEvent, FormEvent, useEffect } from 'react';

import {
  Box,
  Flex,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  rem,
} from '@vimeo/bokeh';
import { Send, CloseX } from '@vimeo/bokeh-icons';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';
import { BokehTooltip } from '@vimeo/live-core/components/bokeh';
import { ISelectedEmoji } from '@vimeo/live-core/components/emoji';
import { browserConfig } from '@vimeo/live-core/configs/BrowserConfig';
import { interactionToolsConfig } from '@vimeo/live-core/configs/InteractionToolsConfig';
import {
  TNotificationHandler,
  useSimpleNotification,
} from '@vimeo/live-core/hooks/live/useActionConfirmation';
import { IQnAContext, QnAManager } from '@vimeo/live-core/store/firebase/interaction/qna';
import {
  trackReplyAndApproveQuestion,
  trackReplyQuestion,
} from '@vimeo/live-core/tracking/tracking_engagement_qna';
import { stopEventPropagation } from '@vimeo/live-core/utils/dom/event';
import { FAILED_STATUS_COLOR } from '@vimeo/live-core/utils/styling/colors';

import { createInteractionDomName } from '../../../../libs/shared/utils/dom/identifying';
import { translations } from '../../../../translations';
import { EmojiButton } from '../../shared/EmojiButton';

interface IQnaReplyFormProps extends IBaseDomElementProps {
  selectedQuestionId: string;
  isApproveNeeded: boolean;
  isDisabled?: boolean;
  isFocused?: boolean;
  qnaContext?: IQnAContext;
  onDismiss: () => void;
}

/**
 * Form to show when have selected question and can reply to it.
 * Used by moderators to add new messages as response to provided questions.
 */
export function QnaReplyForm({
  id = createInteractionDomName('qna-reply-form'),
  className = createInteractionDomName('qna-reply-form'),
  selectedQuestionId,
  isApproveNeeded,
  isDisabled = false,
  isFocused = false,
  onDismiss,
  // eslint-disable-next-line react-hooks/rules-of-hooks
  qnaContext: { qnaActions } = useManager(QnAManager, () => []),
}: IQnaReplyFormProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const [reply, setReply] = useState<string>('');
  const [submitTimeout, setSubmitTimeout] = useState<boolean>(false);

  const canInput: boolean = !isDisabled;
  const canSubmit: boolean = Boolean(
    canInput &&
      reply.length &&
      reply.length <= interactionToolsConfig.QNA.MAX_QUESTION_REPLY_LENGTH &&
      !submitTimeout,
  );
  const charsLeft: number = interactionToolsConfig.QNA.MAX_QUESTION_REPLY_LENGTH - reply.length;
  const isCharsNearLimit: boolean = charsLeft <= 20;
  const isCharsOverLimit: boolean = charsLeft < 0;

  const notify: TNotificationHandler = useSimpleNotification();

  const onSubmitQuestionReply = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (!reply.length || reply.length > interactionToolsConfig.QNA.MAX_QUESTION_REPLY_LENGTH) {
        return;
      }

      if (submitTimeout) {
        return;
      } else {
        setSubmitTimeout(true);
      }

      try {
        await (isApproveNeeded
          ? qnaActions.approveQuestion(selectedQuestionId)
          : Promise.resolve());

        await qnaActions.submitQuestionReply(selectedQuestionId, reply);

        setReply('');

        if (isApproveNeeded) {
          trackReplyAndApproveQuestion();
        } else {
          trackReplyQuestion();
        }
        notify({
          message: isApproveNeeded ? translations.replyPostedAndApproved : translations.replyPosted,
        });

        setTimeout(
          () => setSubmitTimeout(false),
          interactionToolsConfig.QNA.QUESTION_REPLY_THROTTLE,
        );
      } catch (error) {
        setSubmitTimeout(false);
      }
    },
    [qnaActions, isApproveNeeded, notify, reply, selectedQuestionId, submitTimeout],
  );

  const onEmojiSelect = useCallback((selection: ISelectedEmoji) => {
    if (inputRef.current) {
      const cursorPosition = inputRef.current.selectionStart || 0;
      const value = inputRef.current.value;
      const newValue =
        value.slice(0, cursorPosition) +
        selection.native +
        value.slice(cursorPosition, value.length);
      setReply(newValue);
      inputRef.current.focus();
    }
  }, []);

  const onQuestionEdited = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setReply(event.target.value);
  }, []);

  useEffect(() => {
    if (inputRef && inputRef.current) {
      if (isFocused && canInput) {
        inputRef.current.focus();
      } else {
        setReply('');
      }
    }
  }, [isFocused, selectedQuestionId, canInput]);

  return (
    <Box
      id={id}
      className={className}
      as={'form'}
      display={'flex'}
      flexDirection={'column'}
      width={'100%'}
      paddingTop={rem(8)}
      onSubmit={onSubmitQuestionReply}
      onKeyUp={stopEventPropagation}
      onKeyDown={stopEventPropagation}
      onKeyPress={stopEventPropagation}
    >
      <Flex
        id={createInteractionDomName(id, 'title')}
        className={createInteractionDomName(className, 'title')}
        justifyContent={'space-between'}
        alignItems={'center'}
        marginBottom={rem(12)}
        color={canInput ? 'text-primary' : 'text-secondary'}
        fontSize={'text-xs'}
        fontWeight={'bold'}
      >
        <div>{isApproveNeeded ? translations.replyAndApprove : translations.reply}</div>

        <IconButton
          id={createInteractionDomName(id, 'dismiss')}
          className={createInteractionDomName(className, 'dismiss')}
          aria-label={'stop-reply'}
          size={'xs'}
          variant={'minimal'}
          type={'button'}
          icon={<CloseX />}
          onClick={onDismiss}
        />
      </Flex>

      <InputGroup
        id={createInteractionDomName(id, 'controls')}
        className={createInteractionDomName(className, 'controls')}
      >
        {isDisabled ? (
          <BokehTooltip
            placement={'top'}
            label={translations.replyAlreadySent}
            shouldWrapChildren={false}
          >
            <Box position={'absolute'} width={'100%'} height={'100%'} zIndex={2} />
          </BokehTooltip>
        ) : null}

        {browserConfig.BROWSER?.isMobile ? null : (
          <InputLeftElement>
            <EmojiButton
              id={createInteractionDomName(id, 'emoji-button')}
              className={createInteractionDomName(className, 'emoji-button')}
              inputRef={inputRef}
              isSubmitted={submitTimeout}
              placement={'top-start'}
              isDisabled={!canInput}
              onEmojiSelect={onEmojiSelect}
            />
          </InputLeftElement>
        )}

        <Input
          id={createInteractionDomName(id, 'input')}
          className={createInteractionDomName(className, 'input')}
          isDisabled={!canInput}
          ref={inputRef}
          maxLength={interactionToolsConfig.CHAT.MAX_MESSAGE_LENGTH}
          value={reply}
          autoComplete={'off'}
          onChange={onQuestionEdited}
        />

        <InputRightElement>
          <IconButton
            id={createInteractionDomName(id, 'send-button')}
            className={createInteractionDomName(className, 'send-button')}
            aria-label={'qna-submit-button'}
            data-chat-submit
            type={'submit'}
            size={'sm'}
            variant={'minimal'}
            icon={<Send />}
            transition={'none'}
            _hover={{
              backgroundColor: 'transparent!important',
            }}
            isDisabled={!canSubmit}
          />
        </InputRightElement>
      </InputGroup>

      <Flex
        id={createInteractionDomName(id, 'info')}
        className={createInteractionDomName(className, 'info')}
        justifyContent={'flex-end'}
        alignContent={'center'}
        width={'100%'}
      >
        <Flex
          id={createInteractionDomName(id, 'characters-count')}
          className={createInteractionDomName(className, 'characters-count')}
          as={'span'}
          alignItems={'center'}
          fontSize={'text-xs'}
          lineHeight={'text-xs'}
          padding={`${rem(8)} 0 0`}
          fontWeight={isCharsNearLimit || isCharsOverLimit ? 'bold' : 400}
          color={isCharsOverLimit ? FAILED_STATUS_COLOR : 'text-secondary'}
        >
          {`${reply.length}/${interactionToolsConfig.QNA.MAX_QUESTION_REPLY_LENGTH}`}
        </Flex>
      </Flex>
    </Box>
  );
}
